/**
 * WHOChecklistWizard.js
 * Three-phase stepper for the WHO Surgical Safety Checklist.
 * Enforces sequential completion: Sign-In -> Time-Out -> Sign-Out.
 * Each phase delegates rendering to WHOPhaseCard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { WHO_PHASE, WHO_PHASE_LABELS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';
import WHOPhaseCard from './WHOPhaseCard';

const PHASES_ORDER = [WHO_PHASE.SIGN_IN, WHO_PHASE.TIME_OUT, WHO_PHASE.SIGN_OUT];

const PHASE_DESCRIPTIONS = {
  [WHO_PHASE.SIGN_IN]: 'Before induction of anaesthesia',
  [WHO_PHASE.TIME_OUT]: 'Before skin incision',
  [WHO_PHASE.SIGN_OUT]: 'Before patient leaves operating room',
};

const PHASE_LEADS = {
  [WHO_PHASE.SIGN_IN]: 'Led by Anaesthetist',
  [WHO_PHASE.TIME_OUT]: 'Led by Circulating Nurse',
  [WHO_PHASE.SIGN_OUT]: 'Led by Circulating Nurse',
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
  },
  phaseIndicators: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 32,
    padding: '16px 0',
  },
  phaseIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
  },
  phaseCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    transition: 'all 0.3s ease',
    border: '3px solid transparent',
  },
  phaseCircleNotStarted: {
    backgroundColor: '#E0E0E0',
  },
  phaseCircleInProgress: {
    backgroundColor: '#FFF3E0',
    borderColor: '#FF9800',
  },
  phaseCircleComplete: {
    backgroundColor: '#E8F5E9',
    borderColor: '#4CAF50',
  },
  phaseCircleActive: {
    boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.3)',
  },
  phaseNumber: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#999',
  },
  phaseNumberActive: {
    color: '#FF9800',
  },
  phaseLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#666',
    textAlign: 'center',
  },
  phaseLabelActive: {
    color: '#333',
    fontWeight: 600,
  },
  connector: {
    width: 60,
    height: 3,
    backgroundColor: '#E0E0E0',
    marginBottom: 24,
  },
  connectorComplete: {
    backgroundColor: '#4CAF50',
  },
  cardContainer: {
    marginBottom: 24,
  },
  allCompleteBox: {
    textAlign: 'center',
    padding: 32,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
  },
  allCompleteIcon: {
    fontSize: 64,
    color: '#4CAF50',
    marginBottom: 12,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  errorContainer: {
    textAlign: 'center',
    padding: 32,
  },
}));

const WHOChecklistWizard = ({ surgeryId, onComplete }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phaseData, setPhaseData] = useState({
    [WHO_PHASE.SIGN_IN]: {},
    [WHO_PHASE.TIME_OUT]: {},
    [WHO_PHASE.SIGN_OUT]: {},
  });
  const [phaseStatus, setPhaseStatus] = useState({
    [WHO_PHASE.SIGN_IN]: 'not_started',
    [WHO_PHASE.TIME_OUT]: 'not_started',
    [WHO_PHASE.SIGN_OUT]: 'not_started',
  });
  const [activePhase, setActivePhase] = useState(WHO_PHASE.SIGN_IN);
  const [blockDialog, setBlockDialog] = useState({ open: false, targetPhase: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await surgeryService.viewWHOChecklist(surgeryId);
      const data = response.data || {};

      const newPhaseData = {
        [WHO_PHASE.SIGN_IN]: data.sign_in || {},
        [WHO_PHASE.TIME_OUT]: data.time_out || {},
        [WHO_PHASE.SIGN_OUT]: data.sign_out || {},
      };

      const newStatus = {
        [WHO_PHASE.SIGN_IN]: data.sign_in?.completed ? 'complete' : 'not_started',
        [WHO_PHASE.TIME_OUT]: data.time_out?.completed ? 'complete' : 'not_started',
        [WHO_PHASE.SIGN_OUT]: data.sign_out?.completed ? 'complete' : 'not_started',
      };

      // Determine in-progress states
      if (!data.sign_in?.completed && Object.keys(data.sign_in || {}).some((k) => k !== 'completed' && data.sign_in[k])) {
        newStatus[WHO_PHASE.SIGN_IN] = 'in_progress';
      }
      if (!data.time_out?.completed && Object.keys(data.time_out || {}).some((k) => k !== 'completed' && data.time_out[k])) {
        newStatus[WHO_PHASE.TIME_OUT] = 'in_progress';
      }
      if (!data.sign_out?.completed && Object.keys(data.sign_out || {}).some((k) => k !== 'completed' && data.sign_out[k])) {
        newStatus[WHO_PHASE.SIGN_OUT] = 'in_progress';
      }

      setPhaseData(newPhaseData);
      setPhaseStatus(newStatus);

      // Set active phase to first incomplete
      if (newStatus[WHO_PHASE.SIGN_IN] !== 'complete') {
        setActivePhase(WHO_PHASE.SIGN_IN);
      } else if (newStatus[WHO_PHASE.TIME_OUT] !== 'complete') {
        setActivePhase(WHO_PHASE.TIME_OUT);
      } else if (newStatus[WHO_PHASE.SIGN_OUT] !== 'complete') {
        setActivePhase(WHO_PHASE.SIGN_OUT);
      } else {
        setActivePhase(null);
      }
    } catch (err) {
      setError('Failed to load WHO checklist data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [surgeryId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const getPhaseIndex = (phase) => PHASES_ORDER.indexOf(phase);

  const canAccessPhase = (phase) => {
    const idx = getPhaseIndex(phase);
    if (idx === 0) return true;
    const prevPhase = PHASES_ORDER[idx - 1];
    return phaseStatus[prevPhase] === 'complete';
  };

  const handlePhaseClick = (phase) => {
    if (phaseStatus[phase] === 'complete') {
      setActivePhase(phase);
      return;
    }
    if (!canAccessPhase(phase)) {
      setBlockDialog({ open: true, targetPhase: phase });
      return;
    }
    setActivePhase(phase);
  };

  const handlePhaseSave = async (phase, data) => {
    try {
      await surgeryService.saveWHOPhase({
        surgery_id: surgeryId,
        phase,
        ...data,
      });

      setPhaseData((prev) => ({
        ...prev,
        [phase]: { ...prev[phase], ...data, completed: true },
      }));

      setPhaseStatus((prev) => ({
        ...prev,
        [phase]: 'complete',
      }));

      setSnackbar({
        open: true,
        message: `${WHO_PHASE_LABELS[phase]} phase completed successfully`,
        severity: 'success',
      });

      // Advance to next phase
      const idx = getPhaseIndex(phase);
      if (idx < PHASES_ORDER.length - 1) {
        setActivePhase(PHASES_ORDER[idx + 1]);
      } else {
        // All phases complete
        setActivePhase(null);
        if (onComplete) onComplete();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to save ${WHO_PHASE_LABELS[phase]} phase`,
        severity: 'error',
      });
      throw err;
    }
  };

  const getCircleClass = (phase) => {
    const status = phaseStatus[phase];
    let cls = classes.phaseCircle;
    if (status === 'complete') cls += ` ${classes.phaseCircleComplete}`;
    else if (status === 'in_progress') cls += ` ${classes.phaseCircleInProgress}`;
    else cls += ` ${classes.phaseCircleNotStarted}`;
    if (activePhase === phase) cls += ` ${classes.phaseCircleActive}`;
    return cls;
  };

  const allComplete = PHASES_ORDER.every((p) => phaseStatus[p] === 'complete');

  if (loading) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.errorContainer}>
          <Typography color="error" gutterBottom>{error}</Typography>
          <Button variant="outlined" onClick={loadChecklist}>Retry</Button>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>WHO Surgical Safety Checklist</Typography>
        <Typography className={classes.subtitle}>
          Complete all three phases in sequence to ensure patient safety
        </Typography>
      </div>

      {/* Phase indicators */}
      <div className={classes.phaseIndicators}>
        {PHASES_ORDER.map((phase, idx) => (
          <React.Fragment key={phase}>
            {idx > 0 && (
              <div
                className={`${classes.connector} ${
                  phaseStatus[PHASES_ORDER[idx - 1]] === 'complete' ? classes.connectorComplete : ''
                }`}
              />
            )}
            <div
              className={classes.phaseIndicator}
              onClick={() => handlePhaseClick(phase)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handlePhaseClick(phase)}
            >
              <div className={getCircleClass(phase)}>
                {phaseStatus[phase] === 'complete' ? (
                  <CheckCircleIcon style={{ color: '#4CAF50', fontSize: 28 }} />
                ) : (
                  <Typography
                    className={`${classes.phaseNumber} ${
                      activePhase === phase ? classes.phaseNumberActive : ''
                    }`}
                  >
                    {idx + 1}
                  </Typography>
                )}
              </div>
              <Typography
                className={`${classes.phaseLabel} ${
                  activePhase === phase ? classes.phaseLabelActive : ''
                }`}
              >
                {WHO_PHASE_LABELS[phase]}
              </Typography>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Active phase card or all-complete message */}
      <div className={classes.cardContainer}>
        {allComplete ? (
          <div className={classes.allCompleteBox}>
            <CheckCircleIcon className={classes.allCompleteIcon} />
            <Typography variant="h6" style={{ color: '#2E7D32', fontWeight: 600 }}>
              WHO Checklist Complete
            </Typography>
            <Typography variant="body2" style={{ color: '#558B2F', marginTop: 8 }}>
              All three phases have been verified and signed off.
            </Typography>
          </div>
        ) : activePhase ? (
          <WHOPhaseCard
            phase={activePhase}
            data={phaseData[activePhase]}
            onSave={(data) => handlePhaseSave(activePhase, data)}
            disabled={!canAccessPhase(activePhase)}
            surgeryId={surgeryId}
            description={PHASE_DESCRIPTIONS[activePhase]}
            leadRole={PHASE_LEADS[activePhase]}
          />
        ) : null}
      </div>

      {/* Blocking dialog */}
      <Dialog
        open={blockDialog.open}
        onClose={() => setBlockDialog({ open: false, targetPhase: null })}
      >
        <DialogTitle>Phase Not Available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You must complete the{' '}
            <strong>
              {blockDialog.targetPhase &&
                WHO_PHASE_LABELS[PHASES_ORDER[getPhaseIndex(blockDialog.targetPhase) - 1]]}
            </strong>{' '}
            phase before proceeding to{' '}
            <strong>
              {blockDialog.targetPhase && WHO_PHASE_LABELS[blockDialog.targetPhase]}
            </strong>.
            The WHO Surgical Safety Checklist must be completed in sequence.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBlockDialog({ open: false, targetPhase: null })}
            color="primary"
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default WHOChecklistWizard;
